import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CustomReport } from '@app/modules/custom-reports/models/custom-report.model';
import { CustomReportsService } from '@app/modules/custom-reports/services/reports.service';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-custom-reports-data-grid',
    templateUrl: './custom-reports-data-grid.component.html',
    styleUrls: ['./custom-reports-data-grid.component.scss']
})
export class CustomReportsDataGridComponent implements OnInit, OnDestroy {
    @Input() reportType: string;
    public columns: any[] = [
        { field: "name", title: "Name" },
        { field: "description", title: "Description", type: 'longText' },
    ];

    filterCategories: any[] = [
        { field: "name", title: "Name", type: "String", dataType: 'String' },
        { field: "description", title: "Description", type: "String", dataType: 'String' },
    ];

    sortableColumns: any[] = [
        { field: "name", sortValue: "name" },
        { field: "description", sortValue: "description" },
    ];

    public bindingType: String = "array";
    public view: any;
    public gridData: any;
    public gridDataResult: GridDataResult;
    public viewMode: 'cards' | 'table' = 'cards';
    isLoading: boolean;
    pageSize: number = 10;
    skip: number = 0;
    searchValue: string;
    searchFilterString: string;
    getReportsRequest: any;
    sort: SortDescriptor[] = [
        { field: "name", dir: "asc" }
    ];
    sortString: string = 'name-asc';
    filterString: string;
    private searchValueChanged: Subject<string> = new Subject<string>();
    private destroyed: Subject<void> = new Subject<void>();

    constructor(
        private router: Router,
        private reportsService: CustomReportsService,
    ) {
    }

    ngOnInit(): void {
        this.view = this.getReports();

        this.searchValueChanged
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                takeUntil(this.destroyed)
            )
            .subscribe(searchValue => this.search(searchValue));
    }

    ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onSearchInput(): void {
        this.searchValueChanged.next(this.searchValue);
    }

    clearSearch(): void {
        this.searchValue = '';
        this.search(this.searchValue);
    }

    getReports() {
        this.isLoading = true;
        let reportTypeId: string;

        if (this.reportType == 'Terminations') {
            reportTypeId = 'terminations_report';
        } else if (this.reportType == 'Employees') {
            reportTypeId = 'employees_report';
        } else if (this.reportType == 'TimeOff') {
            reportTypeId = 'timeoff_report';
        } else if (this.reportType == 'Configuration') {
            reportTypeId = 'configuration_report';
        } else if (this.reportType == 'Security') {
            reportTypeId = 'security_report';
        } else if (this.reportType == 'Performance') {
            reportTypeId = 'performance_report';
        } else if (this.reportType == 'Toil') {
            reportTypeId = 'toil_report';
        }

        let filter: string = `(Type.Id = "${reportTypeId}")`;
        if (this.filterString) {
            filter = `${this.filterString} AND ${filter}`
        }
        if (this.searchFilterString) {
            filter = `${this.searchFilterString} AND ${filter}`
        }

        this.getReportsRequest = this.reportsService.getNewReports(this.pageSize, this.skip, filter, this.sortString)
            .pipe(
                finalize(() => this.isLoading = false)
            )
            .subscribe(
                res => {
                    this.gridDataResult = {
                        data: res.data,
                        total: res.total,
                    };
                }
            );
    }

    goToRenderReport(dataItem: CustomReport) {
        if (dataItem.url) {
            this.router.navigate([`${dataItem.url}`]);
        }
        else {
            this.router.navigate([`Reports/Detail/${dataItem.id}`]);
        }
    }

    public pageChange(event: PageChangeEvent): void {
        this.skip = event.skip;
        this.pageSize = event.take
        this.view = this.getReports();
    }

    setViewMode(mode: 'cards' | 'table'): void {
        this.viewMode = mode;
    }

    get pageStartIndex(): number {
        return this.gridDataResult?.total ? this.skip + 1 : 0;
    }

    get pageEndIndex(): number {
        if (!this.gridDataResult) {
            return 0;
        }

        return Math.min(this.skip + this.pageSize, this.gridDataResult.total);
    }

    get hasPrevPage(): boolean {
        return this.skip > 0;
    }

    get hasNextPage(): boolean {
        return !!this.gridDataResult && this.skip + this.pageSize < this.gridDataResult.total;
    }

    cardsPagePrev(): void {
        if (!this.hasPrevPage) {
            return;
        }

        this.skip = Math.max(0, this.skip - this.pageSize);
        this.getReports();
    }

    cardsPageNext(): void {
        if (!this.hasNextPage) {
            return;
        }

        this.skip = this.skip + this.pageSize;
        this.getReports();
    }

    search(newSearchString: string) {
        this.searchValue = newSearchString;

        let variants = this.searchValue.split(' ').filter(value => value);
        this.searchFilterString = '';
        variants.map(variant => {
            if (variant) {
                if (this.searchFilterString.length > 2) {
                    this.searchFilterString += ' AND ';
                }
                this.searchFilterString += `(Name like "${variant}")`;
            }
        });

        this.getReportsRequest.unsubscribe();
        this.getReports();
    }

    sortChange(sortString: string) {
        this.sortString = sortString;
        this.getReports();
    }

    filterCallback(filterString: string) {
        this.filterString = filterString;
        this.skip = 0;
        this.getReports();
    }
}
